import React, { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import classNames from 'classnames';
import { MAX_ZOOM, round } from '@wix/seating-common-components';
import s from './plan-scroll-container.scss';
export const GESTURE_SCALE_STEP = 0.065; // different than in dashboard, based on real-life feel testing
export const PlanScrollContainer = forwardRef(({ viewBoxInfo, className, children }, ref) => {
    const [touchPositions, setTouchPositions] = useState({});
    const [startDistance, setStartDistance] = useState(0);
    const [startScale, setStartScale] = useState(1);
    const [touchInfo, setTouchInfo] = useState({ distance: 0, mid: null });
    const gestureDebt = useRef({ x: 0, y: 0, scale: 0 });
    const viewBoxAlreadyChanged = useRef(false);
    const cachedCursorPosition = useRef();
    const { changeSvgViewBox, container, zoom: scale, minZoom } = viewBoxInfo;
    const isPointerDown = useCallback(() => {
        const coordinates = Object.values(touchPositions);
        return Boolean(coordinates.length);
    }, [touchPositions]);
    const getTouchPosition = useCallback((event) => {
        const containerBoundingBox = container.getBoundingClientRect();
        const position = { x: event.clientX, y: event.clientY };
        position.x -= containerBoundingBox.left;
        position.y -= containerBoundingBox.top;
        return position;
    }, [container]);
    const getTouchInfo = useCallback((newTouchPositions) => {
        const positions = Object.values(newTouchPositions ?? touchPositions);
        if (positions.length <= 1) {
            return { distance: 0, mid: positions[0] ?? null };
        }
        const xCoordinates = positions.map(({ x: xCoordinate }) => xCoordinate);
        const xSum = xCoordinates.reduce((sum, value) => sum + value);
        const midX = xSum / xCoordinates.length;
        const yCoordinates = positions.map(({ y: yCoordinate }) => yCoordinate);
        const ySum = yCoordinates.reduce((sum, value) => sum + value);
        const midY = ySum / yCoordinates.length;
        const distances = positions.map(({ x: xCoordinate, y: yCoordinate }) => Math.hypot(Math.abs(midX - xCoordinate), Math.abs(midY - yCoordinate)));
        const distancesSum = distances.reduce((sum, value) => sum + value);
        const averageDistance = distancesSum / distances.length;
        return { distance: averageDistance, mid: { x: midX, y: midY } };
    }, [touchPositions]);
    const onPointerDown = useCallback(event => {
        event.preventDefault();
        const newTouchPositions = {
            ...touchPositions,
            [event.pointerId]: getTouchPosition(event),
        };
        setTouchPositions(newTouchPositions);
        const newTouchInfo = getTouchInfo(newTouchPositions);
        setTouchInfo(newTouchInfo);
        setStartDistance(newTouchInfo.distance);
        setStartScale(scale);
    }, [touchPositions, getTouchPosition, getTouchInfo, scale]);
    const onPointerMove = useCallback(event => {
        event.preventDefault();
        if (!isPointerDown()) {
            return;
        }
        const newTouchPositions = {
            ...touchPositions,
            [event.pointerId]: getTouchPosition(event),
        };
        setTouchPositions(newTouchPositions);
        const pointerIds = Object.keys(touchPositions);
        if (event.pointerId !== Number(pointerIds[pointerIds.length - 1])) {
            return;
        }
        const newTouchInfo = getTouchInfo(newTouchPositions);
        const deltaX = (newTouchInfo.mid.x - touchInfo.mid.x) / scale;
        const deltaY = (newTouchInfo.mid.y - touchInfo.mid.y) / scale;
        const dsFromStart = startDistance && startScale
            ? ((newTouchInfo.distance - startDistance) * (MAX_ZOOM - minZoom)) /
                ((container.clientHeight + container.clientWidth) / 2)
            : 0;
        const deltaScale = startScale + dsFromStart - scale;
        changeSvgViewBox({ deltaX, deltaY, deltaScale, zoomPosition: newTouchInfo.mid });
        setTouchInfo(newTouchInfo);
    }, [
        changeSvgViewBox,
        container,
        getTouchInfo,
        getTouchPosition,
        isPointerDown,
        scale,
        startDistance,
        startScale,
        touchInfo,
        touchPositions,
        minZoom,
    ]);
    const onPointerOut = useCallback(() => {
        setTouchPositions({});
        setStartDistance(null);
        setStartScale(null);
    }, []);
    const executeGesture = useCallback(() => {
        const { x: xDebt, y: yDebt, scale: scaleDebt } = gestureDebt.current;
        const hasDebt = xDebt !== 0 || yDebt !== 0 || scaleDebt !== 0;
        if (hasDebt) {
            viewBoxAlreadyChanged.current = true;
            const fixedScale = round(scale);
            gestureDebt.current.x -= xDebt;
            gestureDebt.current.y -= yDebt;
            gestureDebt.current.scale -= scaleDebt;
            changeSvgViewBox({
                deltaX: xDebt ? round(-xDebt / fixedScale, 0.00001) : 0,
                deltaY: yDebt ? round(-yDebt / fixedScale, 0.00001) : 0,
                zoomPosition: scaleDebt ? cachedCursorPosition.current : null,
                deltaScale: scaleDebt ? scaleDebt : 0,
            });
        }
        else {
            viewBoxAlreadyChanged.current = false;
        }
    }, [changeSvgViewBox, scale]);
    useEffect(() => {
        const eventHandlingContainer = ref.current;
        if (eventHandlingContainer) {
            const onWheel = (event) => {
                event.preventDefault();
                const wheelEvent = event;
                const { deltaX: dx, deltaY: dy, clientX, clientY } = wheelEvent;
                const { left, top } = eventHandlingContainer.getBoundingClientRect();
                cachedCursorPosition.current = { x: clientX - left, y: clientY - top };
                const { x: xDebt, y: yDebt, scale: scaleDebt } = gestureDebt.current;
                const hadDebt = xDebt !== 0 || yDebt !== 0 || scaleDebt !== 0;
                if (wheelEvent.ctrlKey) {
                    gestureDebt.current.scale += dy < 0 ? GESTURE_SCALE_STEP : -GESTURE_SCALE_STEP;
                }
                else {
                    gestureDebt.current.x += dx;
                    gestureDebt.current.y += dy;
                }
                if (!hadDebt && !viewBoxAlreadyChanged.current) {
                    executeGesture();
                }
            };
            eventHandlingContainer.addEventListener('wheel', onWheel, { passive: false });
            return () => eventHandlingContainer.removeEventListener('wheel', onWheel);
        }
        return () => { };
    }, [changeSvgViewBox, scale, ref, executeGesture]);
    useEffect(() => {
        executeGesture();
    }, [executeGesture]);
    return (React.createElement("div", { ref: ref, onPointerDown: onPointerDown, onPointerUp: onPointerOut, onPointerLeave: onPointerOut, onPointerMove: onPointerMove, className: classNames(s.container, className), style: { cursor: isPointerDown() ? 'grabbing' : 'grab' }, "aria-hidden": "true" }, children));
});
//# sourceMappingURL=plan-scroll-container.js.map